import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Field, getIn } from 'formik'
import { GET_LIST, useClient } from '@peracto/client'
import { useSettings } from '@peracto/peracto-hooks'
import AsyncSelect from 'react-select/async'
import debounce from 'debounce-promise'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft'
import { faTimesCircle } from '@fortawesome/pro-regular-svg-icons/faTimesCircle'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'

import { Form, FormActions, FormGroup, Group, Input, Textarea } from '@peracto/peracto-ui'
import SearchEnginePreview from './SearchEnginePreview'
import ArticleAssignment from './ArticleAssignment'

export const MODE_ADD = 'add'
export const MODE_EDIT = 'edit'

const getError = (form, field, label) => {
    let error = getIn(form.errors, field.name)

    if (error) {
        error = error.replace(field.name, label)
    }
    return error
}

const ArticleCategoryForm = ({ mode = MODE_EDIT, values, ...props }) => {
    const { client } = useClient()

    const [showSEOForm, setShowSEOForm] = useState(false)
    const [articleAssignment, setArticleAssignment] = useState(false)

    const { values: p_values } = useSettings()

    const articleCategoryPath = p_values?.sub_urls?.sitemap_article_categories_sub_url || 'articles'

    const fetchArticleCategories = async inputValue => {
        const { data } = await client(GET_LIST, 'article-categories', {
            id: 'article-categories',
            filter: {
                name: inputValue,
            },
        })

        let fieldValues = data

        if (mode === MODE_EDIT) {
            const { id } = values
            fieldValues = fieldValues.filter(articleCat => articleCat.id !== id) // Don't show the current Article Category in the list
        }

        return fieldValues.map(articleCat => ({
            label: articleCat.name,
            value: articleCat.id,
        }))
    }

    const debouncedFetchArticleCategories = debounce(fetchArticleCategories, 200)

    if (articleAssignment) {
        return (
            <>
                <FormActions>
                    <a
                        onClick={() => setArticleAssignment(false)}
                        data-testid="back-to-article-category"
                    >
                        <FontAwesomeIcon icon={faChevronLeft} className="!mr-2" />
                        Back to Article Category
                    </a>
                </FormActions>
                <div data-testid="manage-articles-section">
                    <Group key="manage-articles" id="manage-articles" name="Manage Articles">
                        <ArticleAssignment articleCategory={values} />
                    </Group>
                </div>
            </>
        )
    }

    return (
        <Form
            autoComplete="off"
            values={{
                name: '',
                title: '',
                path: '',
                metaDescription: '',
                ...values,
            }}
            onReset={() => {
                setShowSEOForm(false)
            }}
            {...props}
        >
            <div data-testid="core-section">
                <Group key="core-data" id="core-data" name="Core Data">
                    <Input
                        name="name"
                        testId="category-name"
                        label="Category Name"
                        help="Most typically used for admin only."
                        required
                    />

                    <Input
                        name="locale"
                        placeholder={'en_GB'}
                        testId="locale"
                        label="Locale"
                        required
                    />

                    <Field name="title">
                        {({ field, form }) => (
                            <Input
                                name={field.name}
                                testId="title"
                                label="Title"
                                required
                                onBlur={() => {
                                    if (field?.value?.length > 0) {
                                        if (!form.values.path || form.values.path.length === 0) {
                                            form.setFieldValue(
                                                'path',
                                                encodeURI(
                                                    `${field.value
                                                        .split(' ')
                                                        .join('-')
                                                        .replace(
                                                            /[`~!@#$£€%^&*_|+=?;:'",.()<>\{\}\[\]\\\/]/gi,
                                                            ''
                                                        )
                                                        .replace(/-+/g, '-')
                                                        .toLowerCase()}`
                                                )
                                            )
                                        }
                                    }
                                }}
                            />
                        )}
                    </Field>
                    <Textarea
                        name="shortDescription"
                        testId="description"
                        label="Short Description"
                        help="Available to be displayed on the Article Category template"
                    />

                    <Field name="parent">
                        {({ field, form }) => {
                            const label = 'Parent Category'
                            const error = getError(form, field, label)

                            let value = { label: 'None', value: null }

                            if (field.value) {
                                if (field.value.name) {
                                    value = { label: field.value.name, value: field.value }
                                } else {
                                    value = field.value
                                }
                            }

                            return (
                                <FormGroup
                                    name={field.name}
                                    label={label}
                                    help="Create a hierachy of categories by selecting a parent"
                                    error={error}
                                >
                                    <div className="form-row">
                                        <div
                                            className="col-12 col-md-10"
                                            data-testid="parent-category-dropdown"
                                        >
                                            <AsyncSelect
                                                className="!w-full"
                                                classNamePrefix="list"
                                                loadOptions={input =>
                                                    debouncedFetchArticleCategories(
                                                        input,
                                                        field.value
                                                    )
                                                }
                                                isSearchable={true}
                                                defaultOptions={value}
                                                onChange={option => {
                                                    form.setFieldValue(field.name, option)
                                                }}
                                                value={value}
                                                placeholder="Search for Attributes to add..."
                                                noOptionsMessage={({ inputValue }) => {
                                                    if (inputValue.length > 0) {
                                                        return `No results found for '${inputValue}'.`
                                                    } else {
                                                        return 'Enter text to begin searching.'
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-2 !text-right">
                                            <button
                                                data-testid="clear-parent-category"
                                                className="btn btn-link !px-0"
                                                onClick={e => {
                                                    e.preventDefault()
                                                    form.setFieldValue(field.name, null)
                                                }}
                                                disabled={!field.value}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faTimesCircle}
                                                    className="!mr-2"
                                                />
                                                Clear
                                            </button>
                                        </div>
                                    </div>
                                </FormGroup>
                            )
                        }}
                    </Field>
                </Group>
            </div>

            <div data-testid="seo-section">
                <Group
                    key="search-engine-preview"
                    id="search-engine-preview"
                    name="Search Engine Listing Preview"
                >
                    <div className="!flex !justify-end">
                        <button
                            type="button"
                            className="btn btn-link !px-0"
                            onClick={() => setShowSEOForm(!showSEOForm)}
                            data-testid={`edit-close-seo-toggle`}
                        >
                            {showSEOForm ? 'Close' : 'Edit'}
                        </button>
                    </div>
                    {showSEOForm ? (
                        <>
                            <Field name="path">
                                {({ field, form }) => (
                                    <Input
                                        name={field.name}
                                        label="URL & Path"
                                        prefix={`${process.env.REACT_APP_STOREFRONT_URL ||
                                            window.location.origin}/${articleCategoryPath}/`}
                                        onBlur={() => {
                                            if (field.value?.length > 0) {
                                                const slug = encodeURI(
                                                    field.value
                                                        .split(' ')
                                                        .join('-')
                                                        .replace(/-+/g, '-')
                                                        .toLowerCase()
                                                )
                                                form.setFieldValue(field.name, slug)
                                            }
                                        }}
                                        testId="seo-slug"
                                    />
                                )}
                            </Field>

                            <Field name="metaDescription">
                                {({ field }) => (
                                    <Textarea
                                        name={field.name}
                                        label="Search Engine Description"
                                        charactercount={field?.value?.length || 0}
                                        testId="seo-description"
                                    />
                                )}
                            </Field>
                        </>
                    ) : (
                        <SearchEnginePreview path={articleCategoryPath} />
                    )}
                </Group>
            </div>

            {mode === MODE_EDIT && (
                <div data-testid="manage-articles-section">
                    <Group key="manage-articles" id="manage-articles" name="Manage Articles">
                        <p data-testid="add-remove-text">
                            Add or remove existing articles from this category.
                        </p>

                        <button
                            className="btn btn-primary"
                            onClick={() => setArticleAssignment(true)}
                            data-testid="manage-articles-button"
                        >
                            <FontAwesomeIcon icon={faPlusCircle} className="!mr-2" />
                            Manage Articles
                        </button>
                    </Group>
                </div>
            )}
        </Form>
    )
}

ArticleCategoryForm.displayName = 'ArticleCategoryForm'

ArticleCategoryForm.propTypes = {
    values: PropTypes.object,
    mode: PropTypes.oneOf([MODE_ADD, MODE_EDIT]),
    schema: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
}

export default ArticleCategoryForm
